import type { Post } from '@/@types/post'
import { AttachmentStatus, AttachmentType, FileAttachment } from '@/utils/fetchers/attachment'
import { getMediaMetaQueryKey } from '@/utils/fetchers/media'
import queryClient from '@/utils/queryClient'
import { CDNFile } from '@/utils/upload'
import { zipObject } from 'lodash-es'
import { z } from 'zod'
import { type PostFormValue } from './schemas'

export function toPostFormValue(data: Post): PostFormValue {
	const videoURL = queryClient.getQueryData<string>(getMediaMetaQueryKey(data.id))
	const pictureIds = data.pictures?.map((item) => item.key) ?? []
	const videoIds = videoURL ? [videoURL] : []
	const linkIds = data.linkInfo?.linkUrl ? [data.linkInfo.linkUrl] : []
	return {
		id: data.id,
		content: data.content,
		attachment: {
			ids: [...pictureIds, ...videoIds, ...linkIds],
			map: {
				...zipObject(
					pictureIds,
					data.pictures?.map((item) => ({
						id: item.key,
						type: AttachmentType.PICTURE,
						status: AttachmentStatus.UPLOADED,
						cdn: {
							file: {
								id: item.key,
								fileUrl: item.picUrl,
								key: item.key,
							},
						},
					})) ?? []
				),
				...(videoURL && {
					[videoURL]: {
						id: videoURL,
						type: AttachmentType.VIDEO,
						status: AttachmentStatus.UPLOADED,
						cdn: {
							file: {
								id: videoURL,
								fileUrl: videoURL,
							} as CDNFile,
						},
					},
				}),
				...(data.linkInfo?.linkUrl && {
					[data.linkInfo.linkUrl]: {
						id: data.linkInfo.linkUrl,
						type: AttachmentType.LINK,
						linkInfo: data.linkInfo,
					},
				}),
			},
		},
		topic: data.topic,
	}
}

export function validateVideoAttachmentsStatus(
	videoAttachments: FileAttachment[],
	ctx: z.RefinementCtx
) {
	switch (videoAttachments[0].status) {
		case AttachmentStatus.UPLOAD_FAILED:
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频上传失败，请重试',
			})
			break
		case AttachmentStatus.UPLOADING:
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频上传中，请稍候',
			})
			break
		case AttachmentStatus.WAIT_TO_UPLOAD:
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '请等待视频上传',
			})
			break
		default:
			break
	}
}

export function validatePictureAttachmentsStatus(
	pictureAttachments: FileAttachment[],
	ctx: z.RefinementCtx
) {
	pictureAttachments.forEach((item) => {
		if (item.status !== AttachmentStatus.UPLOADED) {
			switch (item.status) {
				case AttachmentStatus.UPLOAD_FAILED:
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: '图片上传失败，请重试',
					})
					break
				case AttachmentStatus.UPLOADING:
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: '图片上传中，请稍候',
					})
					break
				case AttachmentStatus.WAIT_TO_UPLOAD:
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: '请等待图片上传',
					})
					break
				default:
					break
			}
		}
	})
}
